import { useCallback, useState } from 'react'

import { discoveryApi } from '../api'
import type { Activity } from '../types'
import type { DiscoveryNoticeState } from '../components/discovery-notice'

export function useSaveToggle(
  setItems: (updater: (items: Activity[]) => Activity[]) => void,
) {
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set())
  const [notice, setNotice] = useState<DiscoveryNoticeState | null>(null)

  const toggleSaved = useCallback(
    async (activity: Activity) => {
      if (pendingIds.has(activity.id)) return
      const nextSaved = !activity.saved
      setPendingIds((current) => new Set(current).add(activity.id))
      setItems((current) =>
        current.map((item) =>
          item.id === activity.id ? { ...item, saved: nextSaved } : item,
        ),
      )
      try {
        if (nextSaved) {
          await discoveryApi.saveActivity(activity.id)
        } else {
          await discoveryApi.unsaveActivity(activity.id)
        }
      } catch {
        setItems((current) =>
          current.map((item) =>
            item.id === activity.id ? { ...item, saved: activity.saved } : item,
          ),
        )
        setNotice({ messageKey: 'discovery.notice.saveError', tone: 'error' })
      } finally {
        setPendingIds((current) => {
          const next = new Set(current)
          next.delete(activity.id)
          return next
        })
      }
    },
    [pendingIds, setItems],
  )

  return {
    pendingIds,
    notice,
    dismissNotice: () => setNotice(null),
    toggleSaved,
  }
}
